<template>
    <div>
        <v-data-table
            :headers="headers"
            :items="workOrdersFiltered"
            :item-class="getRowClass"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <h1 class="mx-4">WORK ORDERS</h1>
                    <v-btn rounded color="primary" @click="findWO = true">
                        <v-icon class="mr-1">
                            mdi-database-search
                        </v-icon>
                        FIND
                    </v-btn>
                    <v-spacer />
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.code`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="search"
                    dense
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="projectToFilter"
                    dense
                />
            </template>
            <template v-slot:[`header.type`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="typeToFilter"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.createdBy`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ item.createdBy.name }}</p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.project?.reference }} - {{ item.project?.name }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.productionStart`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.productionStart
                                ? formatDate(item.productionStartFormatted)
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.type`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.assemblyWorkOrder ? 'Assembly' : 'Production' }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.weight`]="{ item }">
                <div class="d-flex justify-end">
                    <p class="my-0">{{ (item.weight || 0).toFixed(1) }} kg</p>
                </div>
            </template>
            <template v-slot:[`item.processes`]="{ item }">
                <div v-for="process in item.processesInfo" :key="process.id">
                    <v-tooltip right>
                        <template v-slot:activator="{ on }">
                            <p class="my-0 text-left" v-on="on">
                                {{ process.process.name }}:
                                {{ process.produced || 0 }}/{{
                                    process.qty
                                }}
                                ({{
                                    (
                                        ((process.produced || 0) * 100) /
                                        process.qty
                                    ).toFixed(0) || 0
                                }}%)
                            </p>
                        </template>
                        <span>
                            {{ process.process.name }}: <br />
                            Total Quantity =
                            {{ process.qty || 0 }}<br />
                            Available = {{ process.available || 0 }} ({{
                                (
                                    ((process.available || 0) * 100) /
                                    process.qty
                                ).toFixed(0) || 0
                            }}%)<br />
                            Produced =
                            {{ process.produced || 0 }} ({{
                                (
                                    ((process.produced || 0) * 100) /
                                    process.qty
                                ).toFixed(0) || 0
                            }}%)<br />
                            Delivered =
                            {{ process.delivered }} ({{
                                (
                                    ((process.delivered || 0) * 100) /
                                    process.qty
                                ).toFixed(0) || 0
                            }}%)
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`item.quality`]="{ item }">
                <div>
                    <v-tooltip right>
                        <template v-slot:activator="{ on }">
                            <v-col>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#2b81d6' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ calculateWOPending(item) }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#2fbf56' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ calculateWOReleased(item) }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#ffa41d' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ calculateWOReprocess(item) }}
                                    </p>
                                </v-btn>
                                <v-btn
                                    x-small
                                    icon
                                    class="mr-1"
                                    color="white"
                                    :style="{ 'background-color': '#FF2610' }"
                                    v-on="on"
                                >
                                    <p class="my-0">
                                        {{ calculateWORejected(item) }}
                                    </p>
                                </v-btn>
                            </v-col>
                        </template>
                        <span>
                            Pending :
                            {{ calculateWOPending(item) || 0 }}
                            <br />
                            Released :
                            {{ calculateWOReleased(item) }} <br />
                            Reprocess :
                            {{ calculateWOReprocess(item) }} <br />
                            Rejected :
                            {{ calculateWORejected(item) }} <br />
                        </span>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:[`item.percentageReceived`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ getPercentageReceived(item) }}</p>
                </div>
            </template>
            <template v-slot:[`item.percentageSent`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">{{ getPercentageSent(item) }}</p>
                </div>
            </template>
            <template v-slot:[`item.alerts`]="{ item }">
                <div
                    v-if="item.alertSummary && item.alertSummary.hasAlerts"
                    class="d-flex align-center justify-center"
                >
                    <v-tooltip v-if="item.alertSummary.open > 0" bottom>
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.open"
                                color="orange"
                                v-on="on"
                            >
                                <v-icon color="orange">mdi-alert-circle</v-icon>
                            </v-badge>
                        </template>
                        <span>{{ item.alertSummary.open }} open alert(s)</span>
                    </v-tooltip>

                    <v-tooltip
                        v-if="item.alertSummary.pendingApproval > 0"
                        bottom
                    >
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.pendingApproval"
                                color="purple"
                                v-on="on"
                                class="ml-2"
                            >
                                <v-icon color="purple">mdi-clock-alert</v-icon>
                            </v-badge>
                        </template>
                        <span
                            >{{ item.alertSummary.pendingApproval }} pending
                            approval</span
                        >
                    </v-tooltip>

                    <v-tooltip v-if="item.alertSummary.resolved > 0" bottom>
                        <template v-slot:activator="{ on }">
                            <v-badge
                                :content="item.alertSummary.resolved"
                                color="green"
                                v-on="on"
                                class="ml-2"
                            >
                                <v-icon color="green">mdi-check-circle</v-icon>
                            </v-badge>
                        </template>
                        <span
                            >{{ item.alertSummary.resolved }} resolved
                            alert(s)</span
                        >
                    </v-tooltip>
                </div>
                <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-menu rounded offset-y>
                    <template v-slot:activator="{ attrs, on }">
                        <v-btn
                            fab
                            v-bind="attrs"
                            v-on="on"
                            small
                            color="grey lighten-3"
                            depressed
                            class="mx-1"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item @click.stop="openProcessWorkOrder(item)">
                            <v-list-item-icon>
                                <v-icon size="20">mdi-eye</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>
                                    View
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item
                            v-if="productionFinishPermission"
                            @click.stop="openFinishProduction(item)"
                        >
                            <v-list-item-icon>
                                <v-icon size="20">mdi-wrench-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>
                                    Finish Production
                                </v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>
        <v-alert
            v-if="activateAlert"
            type="success"
            color="primary"
            :style="{ position: 'absolute', right: '0px', bottom: '0px' }"
        >
            <v-row no-gutters>
                <v-col cols="11">
                    <p class="my-0">{{ alertMessage }}</p>
                </v-col>
                <v-col cols="1">
                    <v-btn
                        small
                        icon
                        class="mt-n1"
                        @click="activateAlert = false"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                </v-col>
            </v-row>
        </v-alert>
        <!--PROCESS WORK ORDER-->
        <v-dialog
            v-model="processWorkOrder"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <OperatorProcessWorkOrder
                v-if="processWorkOrder"
                :order="selectedOrder"
                @close="closeProcessWorkOrder"
            />
        </v-dialog>
        <!--FIND WO-->
        <v-dialog
            v-model="findWO"
            :retain-focus="false"
            persistent
            max-width="600"
        >
            <FindWO v-if="findWO" @addWO="addWO" @close="findWO = false" />
        </v-dialog>
        <!-- close work order -->
        <v-dialog v-model="finishProduction" persistent max-width="400px">
            <v-card :loading="loading">
                <v-card-title class="text-h5">Finish Work Order</v-card-title>
                <v-card-text>
                    Are you sure you want to finish this work order?
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        text
                        color="secondary"
                        @click="closeFinishProduction"
                    >
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="finishWO"
                        text
                        color="primary"
                        :loading="loading"
                    >
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { firestore } from '@/services/firebase'
import API from '@/services/api'
import { mapMutations, mapGetters, mapState, mapActions } from 'vuex'
import WOMethods from './WOMethods.js'

export default {
    name: 'OperatorWOs',
    props: {
        projectId: {
            type: String,
            default: () => undefined,
        },
        project: {
            type: Object,
            deafult: () => ({}),
        },
    },
    components: {
        OperatorProcessWorkOrder: () =>
            import('@/components/WorkOrders/OperatorProcessWorkOrder.vue'),
        FindWO: () => import('@/components/WorkOrders/FindWO.vue'),
    },
    data: () => ({
        selectedOrder: undefined,
        processWorkOrder: false,
        workOrder: false,
        projectToFilter: undefined,
        typeToFilter: undefined,
        loading: false,
        workOrders: [],
        activateAlert: false,
        alertMessage: undefined,
        search: null,
        headers: [
            {
                text: 'ORDER',
                value: 'code',
                align: 'center',
                sortable: false,
                width: 120,
            },
            {
                text: 'CREATED BY',
                value: 'createdBy',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PROJECT',
                value: 'project',
                align: 'center',
                sortable: false,
                width: 240,
            },
            {
                text: 'PRODUCTION START',
                value: 'productionStart',
                align: 'center',
                sortable: false,
            },
            {
                text: 'TYPE',
                value: 'type',
                align: 'center',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'center',
                sortable: false,
            },
            {
                text: 'WEIGHT',
                value: 'weight',
                align: 'right',
                sortable: false,
            },
            {
                text: 'QUALITY',
                value: 'quality',
                align: 'center',
                sortable: false,
            },
            {
                text: 'ALERTS',
                value: 'alerts',
                align: 'center',
                sortable: false,
                width: 100,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                align: 'center',
                sortable: false,
            },
        ],
        company: JSON.parse(localStorage.getItem('company')),
        listener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        enableOptions: true,
        resourceId: undefined,
        showProcesses: false,
        settingProcesses: undefined,
        findWO: false,
        user: {},
        productionFinishPermission: false,
        finishProduction: false,
        qualityProcess: {
            id: 'quality',
            name: 'Quality',
        },
        readyForPackingProcess: {
            id: 'readyForPacking',
            name: 'Ready for packing',
        },
        packingProcess: {
            id: 'packing',
            name: 'Packing',
        },
        users: {},
        projects: {},
        woMethods: new WOMethods(),
        deletedWorkOrdersIds: [],
        alertsListener: null,
    }),
    computed: {
        ...mapState(['notificationResource']),
        workOrdersFiltered() {
            this.openResource()
            let conditions = []
            if (this.search) {
                conditions.push(this.filterOrder)
            }
            if (this.projectToFilter) {
                conditions.push(this.filterProject)
            }
            if (this.typeToFilter) {
                conditions.push(this.filterByType)
            }
            if (conditions.length > 0) {
                return this.workOrders.filter(order => {
                    return conditions.every(condition => {
                        return condition(order)
                    })
                })
            }
            this.sort()
            return this.workOrders
        },
    },
    watch: {
        notificationResource: function(resource) {
            if (resource) {
                this.resourceId = resource
                this.openResource()
                this.setNotificationResource(undefined)
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            this.resourceId = this.$route.query.resourceId
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            // set permission
            this.productionFinishPermission = user.permissions.includes(
                'finishWO'
            )
            if (user.permissions.includes('watchProcesses')) {
                this.showProcesses = true
            }

            if (this.showProcesses) {
                this.headers.splice(6, 0, {
                    text: 'PROCESSES',
                    value: 'processes',
                    align: 'center',
                    sortable: false,
                })
            }

            if (user.process) {
                this.headers.splice(7, 0, {
                    text: '% RECEIVED',
                    value: 'percentageReceived',
                    align: 'center',
                    sortable: false,
                    width: 100,
                })
                this.headers.splice(8, 0, {
                    text: '% SENT',
                    value: 'percentageSent',
                    align: 'center',
                    sortable: false,
                    width: 100,
                })
            }

            const {
                data: { settings },
            } = await API.getSettings()
            this.settingProcesses = settings.find(s => s.name == 'Processes')
            this.projects = await API.getWoProjects()
            const {
                data: { users },
            } = await API.getWoUsers()
            this.users = users
            await this.getWOFromSDK()
            // Load alert summaries for all work orders in batch (initial load)
            await this.loadAlertSummaries()
            // Setup real-time listener for alerts
            this.setupRealtimeAlertsListener()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.listener) {
            this.listener()
            this.listener = null
        }
        if (this.alertsListener) {
            this.alertsListener()
            this.alertsListener = null
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        ...mapActions(['setNotificationResource']),
        ...mapGetters(['getNotificationResource']),
        async finishWO() {
            try {
                this.loading = true
                await API.updateWorkOrder({
                    workOrderId: this.selectedOrder.id,
                    status: 'finished',
                    notificationId: 'pFDh25EcR74EMiuypq8M',
                })
                this.finishProduction = false
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeFinishProduction() {
            this.selectedOrder = {}
            this.finishProduction = false
        },
        openFinishProduction(workOrder) {
            this.selectedOrder = _.cloneDeep(workOrder)
            this.finishProduction = true
        },
        openProcessWorkOrder(item) {
            this.selectedOrder = _.cloneDeep(item)
            this.processWorkOrder = true
        },
        closeProcessWorkOrder() {
            this.processWorkOrder = false
        },
        filterOrder(order) {
            return order.code.toLowerCase().includes(this.search.toLowerCase())
        },
        filterProject(order) {
            return (
                order.project?.name
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase()) ||
                order.project?.reference
                    .toString()
                    .toLowerCase()
                    .includes(this.projectToFilter.toLowerCase())
            )
        },
        filterByType(order) {
            const type = order.assemblyWorkOrder ? 'assembly' : 'production'
            return type.includes(this.typeToFilter.toLowerCase())
        },
        formatDate(seconds) {
            return `${moment.unix(seconds).format('L')} ${moment
                .unix(seconds)
                .format('LT')}`
        },
        sort() {
            this.workOrders.sort(
                (a, b) =>
                    (b.createdOn.seconds || b.createdOn._seconds) -
                    (a.createdOn.seconds || a.createdOn._seconds)
            )
        },
        openResource() {
            if (this.resourceId) {
                const resource = this.workOrders.find(
                    w => w.id == this.resourceId
                )
                if (resource) {
                    this.resourceId = undefined
                    this.openProcessWorkOrder(resource)
                }
            }
        },
        setProcessesInfo(wo) {
            wo.processesInfo = []
            let qualityProcess = {
                process: {
                    id: this.qualityProcess.id,
                    name: this.qualityProcess.name,
                },
                qty: 0,
                received: 0,
                delivered: 0,
                produced: 0,
                available: 0,
            }

            let packingProcess = {
                process: {
                    id: this.packingProcess.id,
                    name: this.packingProcess.name,
                },
                qty: 0,
                received: 0,
                delivered: 0,
                produced: 0,
                available: 0,
            }

            let showDefaultProcesses = false
            const items = wo.items || []
            items.forEach(item => {
                if (item.processes) {
                    item.processes.forEach(process => {
                        let processInfo = wo.processesInfo.find(
                            info => info.process.id == process
                        )

                        if (!processInfo) {
                            let totalDelivered = item.deliveries
                                ? item.deliveries.reduce(
                                      (total, register) =>
                                          register.prevProcess.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0
                            let totalProduced = item.production
                                ? item.production.reduce(
                                      (total, register) =>
                                          register.process.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0

                            let received = item.deliveries
                                ? item.deliveries.filter(
                                      register =>
                                          register.nextProcess.id == process &&
                                          register.receivedBy
                                  )
                                : []
                            let totalReceived = received.reduce(
                                (total, register) => total + register.qty,
                                0
                            )
                            let processIndex = this.settingProcesses.processes.findIndex(
                                p => p.id == process
                            )

                            const pIndex = item.processes.findIndex(
                                p => p == process
                            )
                            if (pIndex == 0) {
                                totalReceived = item.quantity
                            }
                            if (processIndex > -1) {
                                wo.processesInfo.push({
                                    process: {
                                        id: this.settingProcesses.processes[
                                            processIndex
                                        ].id,
                                        name: this.settingProcesses.processes[
                                            processIndex
                                        ].name,
                                    },
                                    qty: item.quantity,
                                    delivered: totalDelivered,
                                    produced: totalProduced,
                                    received: totalReceived,
                                    available: totalReceived - totalProduced,
                                })
                            } else {
                                if (process == this.qualityProcess.id) {
                                    wo.processesInfo.push({
                                        process: this.qualityProcess,
                                        qty: item.quantity,
                                        delivered: totalDelivered,
                                        produced: totalProduced,
                                        received: totalReceived,
                                        available:
                                            totalReceived - totalProduced,
                                    })
                                } else if (process == this.packingProcess.id) {
                                    wo.processesInfo.push({
                                        process: this.packingProcess,
                                        qty: item.quantity,
                                        delivered: totalDelivered,
                                        produced: totalProduced,
                                        received: totalReceived,
                                        available:
                                            totalReceived - totalProduced,
                                    })
                                }
                            }
                        } else {
                            processInfo.qty += item.quantity
                            processInfo.delivered += item.deliveries
                                ? item.deliveries.reduce(
                                      (total, register) =>
                                          register.prevProcess.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0
                            processInfo.produced += item.production
                                ? item.production.reduce(
                                      (total, register) =>
                                          register.process.id == process
                                              ? total + register.qty
                                              : total,
                                      0
                                  )
                                : 0

                            const receivedEntries = item.deliveries
                                ? item.deliveries.filter(
                                      e => e.nextProcess == process
                                  )
                                : []

                            processInfo.received += receivedEntries.reduce(
                                (total, register) => total + register.qty,
                                0
                            )
                        }
                    })

                    let totalReceivedNew = item.deliveries
                        ? item.deliveries.reduce(
                              (total, register) =>
                                  register.nextProcess.id ==
                                      this.packingProcess.id &&
                                  register.receivedBy
                                      ? total + register.qty
                                      : total,
                              0
                          )
                        : 0

                    let totalReceivedOld = item.deliveries
                        ? item.deliveries.reduce(
                              (total, register) =>
                                  register.nextProcess.id ==
                                      this.readyForPackingProcess.id &&
                                  register.receivedBy
                                      ? total + register.qty
                                      : total,
                              0
                          )
                        : 0

                    let totalReceived = totalReceivedOld + totalReceivedNew

                    packingProcess.qty += item.quantity
                    packingProcess.delivered = 'NA'
                    packingProcess.produced += item.packedQty
                    packingProcess.available =
                        totalReceived - (packingProcess.produced || 0)

                    //quality process
                    qualityProcess.qty += item.quantity
                    qualityProcess.delivered = 'NA'
                    qualityProcess.produced =
                        this.calculateRejectedQty(item) +
                        this.calculateReleasedQty(item)
                    qualityProcess.available = this.calculateQualityPending(
                        item,
                        wo
                    )
                }
            })
            if (showDefaultProcesses) {
                wo.processesInfo.push(qualityProcess)
                wo.processesInfo.push(packingProcess)
            }
            if (this.selectedOrder && this.selectedOrder.id == wo.id) {
                this.selectedOrder = _.cloneDeep(wo)
            }
        },
        async addWO(wo) {
            try {
                this.loading = true
                const index = this.workOrders.findIndex(w => w.id == wo.id)
                if (index >= 0) {
                    this.openProcessWorkOrder(this.workOrders[index])
                } else {
                    wo.project = this.projects[wo.projectId] || {}
                    if (!wo.project || Object.keys(wo.project).length == 0) {
                        wo.project = await API.getProject(wo.projectId)
                        this.projects[wo.projectId] = wo.project
                    }
                    wo.projectManager =
                        this.users[wo.project.projectManager] || {}
                    wo.creator =
                        this.users[this.woMethods.createdById(wo.createdBy)] ||
                        {}
                    wo.approvedBy = this.users[wo.approvedBy] || {}
                    this.woMethods.formatItems(wo)
                    this.setProcessesInfo(wo)
                    this.workOrders.splice(0, 0, wo)
                    this.openProcessWorkOrder(wo)
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        getPercentageReceived(wo) {
            let totalReceived = 0
            let totalItems = 0
            wo.items.forEach(item => {
                const processIndex = item.processes.findIndex(
                    processId => this.user.process.id == processId
                )
                if (processIndex > -1) {
                    totalItems += item.quantity
                    if (processIndex == 0) {
                        totalReceived += Number(item.quantity)
                    } else if (processIndex > 0 && item.deliveries) {
                        for (let entry of item.deliveries) {
                            if (
                                entry.nextProcess.id == this.user.process.id &&
                                entry.receivedBy
                            ) {
                                totalReceived += Number(entry.qty)
                            }
                        }
                    }
                }
            })
            const percentage = ((totalReceived / totalItems) * 100).toFixed(1)
            return isNaN(percentage) ? '-' : `${percentage}%`
        },
        getPercentageSent(wo) {
            let totalSent = 0
            let totalItems = 0
            wo.items.forEach(item => {
                const processIndex = item.processes.findIndex(
                    processId => this.user.process.id == processId
                )
                if (processIndex > -1) {
                    totalItems += item.quantity
                    if (item.deliveries) {
                        for (let entry of item.deliveries) {
                            if (entry.prevProcess.id == this.user.process.id) {
                                totalSent += Number(entry.qty)
                            }
                        }
                    }
                }
            })
            const percentage = ((totalSent / totalItems) * 100).toFixed(1)
            return isNaN(percentage) ? '-' : `${percentage}%`
        },
        calculateQualityPending(item, workOrder) {
            try {
                let pendingQty = 0
                let lastProcessIndex = item.processes.length - 1
                if (workOrder.assemblyWorkOrder != undefined) {
                    lastProcessIndex =
                        item.processes.findIndex(
                            p => p == this.qualityProcess.id
                        ) - 1
                }
                if (lastProcessIndex > -1) {
                    const lastProductionProcess =
                        item.processes[lastProcessIndex]
                    if (item.production) {
                        const production = item.production.filter(
                            entry => entry.process.id == lastProductionProcess
                        )
                        if (production.length > 0) {
                            pendingQty = production.reduce(
                                (accumulator, entry) => accumulator + entry.qty,
                                0
                            )
                        }
                    }
                    if (item.quality) {
                        const rejected = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.rejectQty || 0),
                            0
                        )
                        const released = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.releaseQty || 0),
                            0
                        )
                        pendingQty -=
                            Number(released || 0) + Number(rejected || 0)
                    }
                }

                return pendingQty
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateReleasedQty(item) {
            try {
                let released = 0
                if (item.quality) {
                    const value = item.quality.reduce(
                        (accumulator, entry) =>
                            accumulator + (entry.releaseQty || 0),
                        0
                    )
                    released += Number(value || 0)
                }
                return released
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateRejectedQty(item) {
            try {
                let rejected = 0
                if (item.quality) {
                    const value = item.quality.reduce(
                        (accumulator, entry) =>
                            accumulator + (entry.rejectQty || 0),
                        0
                    )
                    rejected += Number(value || 0)
                }
                return rejected
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWOPending(wo) {
            try {
                let totalPending = 0
                wo.items.forEach(item => {
                    let pendingQty = 0
                    let lastProcessIndex = item.processes.length - 1
                    if (wo.assemblyWorkOrder != undefined) {
                        lastProcessIndex =
                            item.processes.findIndex(
                                p => p == this.qualityProcess.id
                            ) - 1
                    }
                    if (lastProcessIndex > -1) {
                        const lastProductionProcess =
                            item.processes[lastProcessIndex]
                        if (item.production) {
                            const production = item.production.filter(
                                entry =>
                                    entry.process.id == lastProductionProcess
                            )
                            if (production.length > 0) {
                                pendingQty = production.reduce(
                                    (accumulator, entry) =>
                                        accumulator + entry.qty,
                                    0
                                )
                            }
                        }
                        if (item.quality) {
                            const rejected = item.quality.reduce(
                                (accumulator, entry) =>
                                    accumulator + (entry.rejectQty || 0),
                                0
                            )
                            const released = item.quality.reduce(
                                (accumulator, entry) =>
                                    accumulator + (entry.releaseQty || 0),
                                0
                            )
                            pendingQty -=
                                Number(released || 0) + Number(rejected || 0)
                        }
                    }
                    totalPending += pendingQty
                })

                return totalPending
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWOReleased(wo) {
            try {
                let totalReleased = 0
                wo.items.forEach(item => {
                    let released = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.releaseQty || 0),
                            0
                        )
                        released += Number(value || 0)
                    }
                    totalReleased += released
                })
                return totalReleased
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWOReprocess(wo) {
            try {
                let totalReprocess = 0
                wo.items.forEach(item => {
                    let reprocess = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.reprocessQty || 0),
                            0
                        )
                        reprocess += Number(value || 0)
                    }
                    totalReprocess += reprocess
                })

                return totalReprocess
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        calculateWORejected(wo) {
            try {
                let totalRejected = 0
                wo.items.forEach(item => {
                    let rejected = 0
                    if (item.quality) {
                        const value = item.quality.reduce(
                            (accumulator, entry) =>
                                accumulator + (entry.rejectQty || 0),
                            0
                        )
                        rejected += Number(value || 0)
                    }
                    totalRejected += rejected
                })

                return totalRejected
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async getWOFromSDK() {
            try {
                this.loading = true

                let startDate = new Date()
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('workOrders')
                    .where('status', 'in', [
                        'rejected',
                        'approved',
                        'finished',
                        'deleted',
                    ])
                    .where('updatedOn', '>=', startDate)

                let initialData = await this.loadAllActiveWorkOrders()

                let data = []
                //filter by status
                initialData = initialData.filter(wo =>
                    ['approved'].includes(wo.status)
                )
                for (let wo of initialData) {
                    wo = await this.formatWorkOrder(wo)
                    if (wo) {
                        data.push(wo)
                    }
                }

                // Inicializar workOrders con los datos cargados antes de configurar el listener en tiempo real
                this.workOrders = [...data]

                this.listener = query.onSnapshot(async snapshot => {
                    const changes = Array.from(snapshot.docChanges())
                    const processedChanges = await Promise.all(
                        changes.map(async change => {
                            let wo = Object.assign(change.doc.data(), {
                                id: change.doc.id,
                            })
                            wo = await this.formatWorkOrder(wo)
                            return { change, wo }
                        })
                    )

                    // Procesar todos los cambios después de que todos se hayan formateado
                    processedChanges.forEach(({ change, wo }) => {
                        if (wo) {
                            if (
                                change.type === 'added' ||
                                change.type === 'modified'
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    // Usar $set para asegurar reactividad de Vue
                                    this.$set(data, index, wo)
                                } else {
                                    data.push(wo)
                                }
                            }
                            if (
                                change.type === 'removed' ||
                                wo.status !== 'approved'
                            ) {
                                const index = data.findIndex(w => w.id == wo.id)
                                if (index >= 0) {
                                    data.splice(index, 1)
                                }
                            }
                        }
                    })
                    // Forzar actualización del array para asegurar reactividad
                    this.workOrders = [...data]
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async formatWorkOrder(wo) {
            try {
                let workOrder = wo
                workOrder.productionStartFormatted = workOrder.productionStart
                    ? workOrder.productionStart.seconds ||
                      workOrder.productionStart._seconds
                    : undefined
                workOrder.approvedDateFormatted = workOrder.approvedDate
                    ? workOrder.approvedDate.seconds ||
                      workOrder.approvedDate._seconds
                    : undefined

                this.woMethods.assignCreatedBy(workOrder, this.users)

                // get or fetch project
                workOrder.project = this.projects[workOrder.projectId] || {}
                if (
                    !workOrder.project ||
                    Object.keys(workOrder.project).length == 0
                ) {
                    workOrder.project = await API.getProject(
                        workOrder.projectId
                    )
                    // add project to projects object
                    this.projects[workOrder.projectId] = workOrder.project
                }

                workOrder = this.woMethods.formatItems(workOrder)
                this.setProcessesInfo(workOrder)

                // update selectedOrder if it's currently open
                if (
                    this.selectedOrder &&
                    this.selectedOrder.id &&
                    this.selectedOrder.id == workOrder.id
                ) {
                    this.selectedOrder = _.cloneDeep(workOrder)
                }
                return workOrder
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        async loadAllActiveWorkOrders() {
            return await this.woMethods.loadAllActiveWorkOrders(
                this.companyId,
                ['rejected', 'approved', 'finished', 'deleted']
            )
        },
        async loadAlertSummaries() {
            // Load alert summaries for all work orders in a single batch call
            console.log('in')
            console.log('workOrders', this.workOrders)
            if (this.workOrders.length === 0) return
            console.log('in2')
            try {
                const workOrderIds = this.workOrders.map(wo => wo.id)
                const summaries = await API.getAlertsSummaries(workOrderIds)

                console.log('summaries', summaries)

                // Attach summaries to work orders
                this.workOrders.forEach(wo => {
                    const summary = summaries[wo.id] || {
                        total: 0,
                        open: 0,
                        resolved: 0,
                        hasAlerts: false,
                        allResolved: false,
                    }
                    this.$set(wo, 'alertSummary', summary)
                })
            } catch (error) {
                // If error, set empty summaries
                this.workOrders.forEach(wo => {
                    const emptySummary = {
                        total: 0,
                        open: 0,
                        resolved: 0,
                        hasAlerts: false,
                        allResolved: false,
                    }
                    this.$set(wo, 'alertSummary', emptySummary)
                })
            }
        },
        setupRealtimeAlertsListener() {
            try {
                // Setup real-time listener for alerts
                const alertsCollection = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('alerts')

                this.alertsListener = alertsCollection.onSnapshot(
                    snapshot => {
                        // Create a map of workOrderId -> alert summaries
                        const summariesMap = {}

                        // Process all documents in the alerts collection
                        snapshot.docs.forEach(doc => {
                            const data = doc.data()
                            const workOrderId = doc.id

                            // Initialize summary for this work order
                            if (!summariesMap[workOrderId]) {
                                summariesMap[workOrderId] = {
                                    total: 0,
                                    open: 0,
                                    resolved: 0,
                                    hasAlerts: false,
                                    allResolved: false,
                                }
                            }

                            // Extract all alerts from the alerts object
                            if (
                                data.alerts &&
                                typeof data.alerts === 'object'
                            ) {
                                Object.keys(data.alerts).forEach(alertId => {
                                    const alert = data.alerts[alertId]
                                    summariesMap[workOrderId].total++

                                    if (alert.status === 'resolved') {
                                        summariesMap[workOrderId].resolved++
                                    } else {
                                        summariesMap[workOrderId].open++
                                    }
                                })
                            }

                            // Set flags
                            if (summariesMap[workOrderId].total > 0) {
                                summariesMap[workOrderId].hasAlerts = true
                                summariesMap[workOrderId].allResolved =
                                    summariesMap[workOrderId].open === 0
                            }
                        })

                        // Update alert summaries for existing work orders
                        this.workOrders.forEach(wo => {
                            const summary = summariesMap[wo.id] || {
                                total: 0,
                                open: 0,
                                resolved: 0,
                                hasAlerts: false,
                                allResolved: false,
                            }
                            this.$set(wo, 'alertSummary', summary)
                        })
                    },
                    error => {
                        console.error('Error in alerts listener:', error)
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                )
            } catch (error) {
                console.error('Error setting up alerts listener:', error)
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        getRowClass(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                pendingApproval: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return ''

            if (summary.pendingApproval > 0) {
                return 'alert-pending-approval-row'
            }
            if (summary.open > 0) {
                return 'alert-open-row'
            }
            if (summary.allResolved && summary.total > 0) {
                return 'alert-resolved-row'
            }
            return ''
        },
        getAlertIconColor(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return 'grey'
            if (summary.allResolved) return 'blue'
            return 'orange'
        },
        getAlertTooltip(item) {
            const summary = item.alertSummary || {
                total: 0,
                open: 0,
                resolved: 0,
                hasAlerts: false,
                allResolved: false,
            }
            if (!summary.hasAlerts) return ''
            if (summary.allResolved) {
                return `All ${summary.total} alert(s) resolved`
            }
            return `${summary.open} open alert(s), ${summary.resolved} resolved`
        },
    },
}
</script>

<style>
.v-input .v-label {
    font-size: 12px;
}
.alert-open-row {
    background-color: #fff9e6 !important;
}
.alert-resolved-row {
    background-color: #e6f3ff !important;
}
.alert-pending-approval-row {
    background-color: #f3e5f5 !important;
}
</style>
